/** @file Dopesheet row headers: a virtualized DOM list beside the canvas; group rows fold their channels. */
import type { TimelineRow } from '@pose2d/editor-geometry/types';
import type { Translate } from '@pose2d/i18n/types';
import { visibleRange } from '../../ui-kit/logic/visibleRange';
import { DOPE_PX } from './draw/dopeMetrics';
import { rowLabel } from './rowLabel';

/** Props of RowHeaders; `height` is the canvas height (ruler included), `scrollY` the shared row scroll. */
export interface RowHeadersProps {
  rows: readonly TimelineRow[];
  scrollY: number;
  height: number;
  t: Translate;
  onToggle: (target: string) => void;
  onScroll: (y: number) => void;
}

function HeaderRow({ row, top, t, onToggle }: { row: TimelineRow; top: number; t: Translate; onToggle: (target: string) => void }) {
  const label = rowLabel(row, t);
  const fold = row.kind === 'group' && row.channels.length > 0;
  return (
    <div role="listitem" className={row.kind === 'group' ? 'timeline-row timeline-row-group' : 'timeline-row'} style={{ top, height: DOPE_PX.row }}>
      {fold ? (
        <button type="button" className="timeline-fold" aria-expanded={!row.collapsed} aria-label={t(row.collapsed ? 'panels.timeline.expand' : 'panels.timeline.collapse', { name: label })} onClick={() => { onToggle(row.target); }}>
          {row.collapsed ? '▸' : '▾'}
        </button>
      ) : null}
      <span className="timeline-row-label">{label}</span>
    </div>
  );
}

/** Renders only the rows in view (4 rows of overscan); the wheel scrolls rows like the canvas does. */
export function RowHeaders({ rows, scrollY, height, t, onToggle, onScroll }: RowHeadersProps) {
  const body = Math.max(0, height - DOPE_PX.ruler);
  const { start, end } = visibleRange({ scrollTop: scrollY, viewportHeight: body, rowHeight: DOPE_PX.row, count: rows.length, overscan: 4 });
  return (
    <div className="timeline-headers" role="list" aria-label={t('panels.timeline.rows')} onWheel={(e) => { onScroll(scrollY + e.deltaY); }}>
      <div className="timeline-headers-ruler" style={{ height: DOPE_PX.ruler }} />
      <div className="timeline-headers-rows" style={{ height: body }}>
        {rows.slice(start, end).map((row, i) => (
          <HeaderRow key={`${row.target}|${row.channel ?? '*'}`} row={row} top={(start + i) * DOPE_PX.row - scrollY} t={t} onToggle={onToggle} />
        ))}
      </div>
    </div>
  );
}
